import React, { useEffect, useState, useCallback } from 'react';
import sequenceService, {
  Sequence,
  SequenceStepDef,
  SequenceChannel,
  SEQUENCE_CHANNEL_LABELS,
} from '../services/sequenceService';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, RelatedLinks, type RelatedLinkItem } from '../components/ui';
import { useKampagnenZuordnung, kampagnenLinks } from '../vertrieb/kampagnen';
import icpCampaignService, { type IcpCampaign } from '../services/icpCampaignService';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

const CHANNELS = Object.keys(SEQUENCE_CHANNEL_LABELS) as SequenceChannel[];

// Welche Felder ein Schritt-Typ in der UI braucht.
const usesSubject = (t: SequenceChannel) => t === 'email';
const usesBody = (t: SequenceChannel) =>
  t === 'email' || t === 'linkedinSend' || t === 'linkedinInvite' || t === 'manual';
const bodyLabel = (t: SequenceChannel) =>
  t === 'email'
    ? 'Nachricht'
    : t === 'manual'
    ? 'Beschreibung / Titel'
    : t === 'linkedinInvite'
    ? 'Einladungsnotiz (optional)'
    : 'Nachricht';

const newStep = (): SequenceStepDef => ({ type: 'email', delay: 0, subject: '', body: '' });

const SequencesPage: React.FC = () => {
  const { confirm } = useConfirm();
  // Welle 2: Anlegen, Bearbeiten, Loeschen, Push und Resync laufen alle unter
  // versand.ausfuehren (es gibt kein versand.bearbeiten). Ohne das Recht
  // bleibt der Editor als Lesesicht stehen, Felder gesperrt, Knoepfe weg.
  const { darf } = useBerechtigung();
  const darfAusfuehren = darf('versand.ausfuehren');
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string; steps: SequenceStepDef[] }>({
    name: '',
    description: '',
    steps: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selected = sequences.find((s) => s.id === selectedId) || null;
  // ?id=<Sequenz>: Sprung aus ICP-Kampagne oder Lead-Liste.
  const [searchParams] = useSearchParams();
  const presetId = searchParams.get('id');
  const zuordnung = useKampagnenZuordnung();
  // Welche ICP-Kampagnen diese Sequenz nutzen (auch vor der Lemlist-Uebergabe).
  const [icpKampagnen, setIcpKampagnen] = useState<IcpCampaign[]>([]);
  useEffect(() => {
    Promise.resolve(icpCampaignService.list())
      .then((liste) => setIcpKampagnen(Array.isArray(liste) ? liste : []))
      .catch(() => setIcpKampagnen([]));
  }, []);
  const verwendetIn: RelatedLinkItem[] = selected ? [
    ...icpKampagnen.filter((k) => k.sequenceId === selected.id).map((k) => ({
      key: `icp-${k.id}`, label: k.name, icon: 'target' as const, to: `/icp-campaigns/${encodeURIComponent(k.id)}`,
      title: `ICP-Kampagne „${k.name}“ öffnen`,
    })),
    ...(selected.lemlistCampaignId
      ? kampagnenLinks(selected.lemlistCampaignId, zuordnung, { leads: true, omit: ['sequenz'] })
        .filter((l) => !icpKampagnen.some((k) => k.sequenceId === selected.id && l.key === `icp-${k.id}`))
      : []),
  ] : [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await sequenceService.list();
      setSequences(list);
      setSelectedId((prev) => prev
        ?? (presetId && list.some((s) => s.id === presetId) ? presetId : null)
        ?? (list[0]?.id ?? null));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
    // presetId nur beim ersten Laden; danach waehlt die Liste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Draft aus der ausgewählten Sequenz befüllen.
  useEffect(() => {
    if (selected) {
      setDraft({
        name: selected.name,
        description: selected.description || '',
        steps: selected.steps.map((s) => ({ ...s })),
      });
    }
  }, [selected]);

  const handleCreate = async () => {
    setError(null);
    try {
      const seq = await sequenceService.create({
        name: 'Neue Sequenz',
        steps: [newStep()],
      });
      setSequences((prev) => [seq, ...prev]);
      setSelectedId(seq.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (seq: Sequence) => {
    const ok = await confirm({
      title: `Sequenz „${seq.name}“ löschen?`,
      message: 'Die Sequenz wird mit allen Schritten dauerhaft gelöscht. Rückgängig nur durch Neuanlage.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await sequenceService.remove(seq.id);
      setSequences((prev) => prev.filter((s) => s.id !== seq.id));
      if (selectedId === seq.id) setSelectedId(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await sequenceService.update(selectedId, draft);
      setSequences((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setNotice('Gespeichert.');
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePush = async () => {
    if (!selectedId) return;
    const ok = await confirm({
      title: 'Als Lemlist-Kampagne anlegen?',
      message: 'Die gespeicherten Schritte werden als echte Kampagne in Lemlist angelegt; ungespeicherte Änderungen am Entwurf werden nicht übertragen. Rückgängig nur durch Löschen der Kampagne in Lemlist.',
      confirmLabel: 'Anlegen',
    });
    if (!ok) return;
    setPushing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await sequenceService.pushToLemlist(selectedId);
      setNotice(
        `${res.dryRun ? '[Dry-Run] ' : ''}An Lemlist übertragen — Kampagne ${res.campaignId}, ${res.stepsCreated} Schritte.`
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setPushing(false);
    }
  };

  const handleResync = async () => {
    if (!selectedId) return;
    const ok = await confirm({
      title: 'Lemlist-Kampagne aktualisieren?',
      message: 'Alle Schritte der bestehenden Lemlist-Kampagne werden gelöscht und durch die gespeicherten Schritte ersetzt; ungespeicherte Änderungen am Entwurf werden nicht übertragen. Rückgängig nur durch einen erneuten Abgleich.',
      confirmLabel: 'Ersetzen',
      tone: 'danger',
    });
    if (!ok) return;
    setResyncing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await sequenceService.resyncToLemlist(selectedId);
      setNotice(
        `${res.dryRun ? '[Dry-Run] ' : ''}Lemlist aktualisiert — ${res.stepsDeleted} entfernt, ${res.stepsCreated} neu angelegt.`
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setResyncing(false);
    }
  };

  // --- Step-Mutationen (lokaler Draft) ---
  const updateStep = (i: number, patch: Partial<SequenceStepDef>) =>
    setDraft((d) => ({
      ...d,
      steps: d.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  const addStep = () => setDraft((d) => ({ ...d, steps: [...d.steps, newStep()] }));
  const removeStep = (i: number) =>
    setDraft((d) => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.steps.length) return d;
      const steps = [...d.steps];
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...d, steps };
    });

  const alreadyPushed = !!selected?.lemlistCampaignId;

  return (
    <div className="page-container-wide">
      <PageHeader
        title="Sequenzen"
        subtitle={routeById('sequences')?.description}
        actions={darfAusfuehren ? (
          <button className="btn btn-primary" onClick={handleCreate}>
            Neue Sequenz
          </button>
        ) : undefined}
      />
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
        Eigene mehrstufige Outreach-Sequenzen bauen und als Lemlist-Kampagne übertragen.
      </p>
      {!darfAusfuehren && (
        <p className="text-sm text-muted" data-testid="rechte-hinweis" style={{ marginBottom: 'var(--space-4)' }}>
          Anlegen, Bearbeiten und Übertragen sind {rollenAufzaehlung(rollenMitRecht('versand.ausfuehren'))} vorbehalten.
        </p>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="alert alert-success" style={{ marginBottom: 'var(--space-4)' }}>
          {notice}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 'var(--space-5)' }}>
        {/* Liste */}
        <div className="card" style={{ alignSelf: 'start' }}>
          <h3 className="card-title">Meine Sequenzen</h3>
          {loading ? (
            <p className="text-muted">Lade…</p>
          ) : sequences.length === 0 ? (
            <p className="text-muted text-sm">Noch keine Sequenz.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {sequences.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`btn ${s.id === selectedId ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ justifyContent: 'space-between', textAlign: 'left' }}
                >
                  <span>{s.name}</span>
                  {s.lemlistCampaignId && (
                    <span className="badge badge-success" title="An Lemlist übertragen">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        {selected ? (
          <div className="card">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                className="form-input"
                value={draft.name}
                disabled={!darfAusfuehren}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Beschreibung (optional)</label>
              <input
                className="form-input"
                value={draft.description}
                disabled={!darfAusfuehren}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>

            <p className="text-xs text-muted">
              Variablen: <code>{'{{firstName}}'}</code> <code>{'{{lastName}}'}</code>{' '}
              <code>{'{{companyName}}'}</code> <code>{'{{icebreaker}}'}</code>{' '}
              <code>{'{{icebreakerLinkedin}}'}</code> — werden je Lead in Lemlist ersetzt.
            </p>

            {/* Schritte */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', margin: 'var(--space-4) 0' }}>
              {draft.steps.map((step, i) => (
                <div
                  key={i}
                  className="card"
                  style={{ background: 'var(--color-bg-subtle, #f8fafc)', padding: 'var(--space-3)' }}
                >
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Schritt {i + 1}</label>
                      <select
                        className="form-select"
                        value={step.type}
                        disabled={!darfAusfuehren}
                        onChange={(e) => updateStep(i, { type: e.target.value as SequenceChannel })}
                      >
                        {CHANNELS.map((c) => (
                          <option key={c} value={c}>
                            {SEQUENCE_CHANNEL_LABELS[c]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0, maxWidth: '120px' }}>
                      <label className="form-label">Warten (Tage)</label>
                      <input
                        type="number"
                        min={0}
                        max={1500}
                        className="form-input"
                        value={step.delay}
                        disabled={!darfAusfuehren}
                        onChange={(e) => updateStep(i, { delay: parseInt(e.target.value, 10) || 0 })}
                      />
                    </div>
                    {darfAusfuehren && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => moveStep(i, -1)} disabled={i === 0} title="Nach oben">
                        ↑
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => moveStep(i, 1)}
                        disabled={i === draft.steps.length - 1}
                        title="Nach unten"
                      >
                        ↓
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => removeStep(i)} title="Entfernen">
                        ✕
                      </button>
                    </div>
                    )}
                  </div>

                  {usesSubject(step.type) && (
                    <div className="form-group" style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
                      <label className="form-label">Betreff</label>
                      <input
                        className="form-input"
                        value={step.subject || ''}
                        disabled={!darfAusfuehren}
                        onChange={(e) => updateStep(i, { subject: e.target.value })}
                      />
                    </div>
                  )}
                  {usesBody(step.type) && (
                    <div className="form-group" style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
                      <label className="form-label">{bodyLabel(step.type)}</label>
                      <textarea
                        className="form-textarea"
                        rows={3}
                        value={step.body || ''}
                        disabled={!darfAusfuehren}
                        onChange={(e) => updateStep(i, { body: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              ))}
              {darfAusfuehren && (
                <button className="btn btn-secondary" onClick={addStep}>
                  + Schritt hinzufügen
                </button>
              )}
            </div>

            {verwendetIn.length > 0 && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <div className="text-sm text-secondary" style={{ marginBottom: 'var(--space-1)' }}>Verwendet in</div>
                <RelatedLinks items={verwendetIn} label={`Verknüpft mit Sequenz ${selected.name}`} />
              </div>
            )}

            {alreadyPushed && (
              <div className="alert alert-info" style={{ marginBottom: 'var(--space-3)' }}>
                Übertragen an Lemlist-Kampagne <code>{selected.lemlistCampaignId}</code>
                {selected.lemlistPushedAt
                  ? ` am ${new Date(selected.lemlistPushedAt).toLocaleString('de-DE')}`
                  : ''}
{darfAusfuehren ? '. Nach Änderungen: erst „Speichern", dann „In Lemlist aktualisieren".' : '.'}
              </div>
            )}

            {darfAusfuehren && (
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Speichere…' : 'Speichern'}
              </button>
              {alreadyPushed ? (
                <button
                  className="btn btn-secondary"
                  onClick={handleResync}
                  disabled={resyncing || draft.steps.length === 0}
                  title="Schritte der bestehenden Lemlist-Kampagne durch die aktuellen ersetzen"
                >
                  {resyncing ? 'Aktualisiere…' : 'In Lemlist aktualisieren'}
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  onClick={handlePush}
                  disabled={pushing || draft.steps.length === 0}
                  title="Als echte Lemlist-Kampagne anlegen"
                >
                  {pushing ? 'Übertrage…' : 'An Lemlist übertragen'}
                </button>
              )}
              <button
                className="btn btn-ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => handleDelete(selected)}
              >
                Löschen
              </button>
            </div>
            )}
          </div>
        ) : (
          <div className="card">
            <p className="text-muted">
              {darfAusfuehren ? 'Wähle links eine Sequenz oder lege eine neue an.' : 'Wähle links eine Sequenz.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SequencesPage;
